// Mortgage and real estate investment calculation formulas

/**
 * Calculate monthly mortgage payment (P&I only)
 */
export function calculateMonthlyPayment(principal: number, annualRate: number, termYears: number): number {
  if (principal <= 0 || termYears <= 0) return 0
  if (annualRate === 0) return principal / (termYears * 12)

  const monthlyRate = annualRate / 100 / 12
  const numPayments = termYears * 12
  const payment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) / (Math.pow(1 + monthlyRate, numPayments) - 1)

  return payment
}

/**
 * Calculate total interest paid over life of loan
 */
export function calculateTotalInterest(principal: number, monthlyPayment: number, termYears: number): number {
  const totalPaid = monthlyPayment * termYears * 12
  return totalPaid - principal
}

/**
 * Calculate DSCR (Debt Service Coverage Ratio)
 */
export function calculateDSCR(monthlyRent: number, monthlyDebtService: number): number {
  if (monthlyDebtService <= 0) return 0
  return monthlyRent / monthlyDebtService
}

/**
 * Calculate maximum loan amount for target DSCR
 */
export function calculateMaxLoanForDSCR(
  monthlyRent: number,
  targetDSCR: number,
  annualRate: number,
  termYears: number
): number {
  if (targetDSCR <= 0 || annualRate <= 0 || termYears <= 0) return 0

  const maxMonthlyPayment = monthlyRent / targetDSCR
  const monthlyRate = annualRate / 100 / 12
  const numPayments = termYears * 12

  // Reverse the mortgage formula to find principal
  const principal =
    (maxMonthlyPayment * (Math.pow(1 + monthlyRate, numPayments) - 1)) /
    (monthlyRate * Math.pow(1 + monthlyRate, numPayments))

  return principal
}

/**
 * Calculate Cap Rate
 */
export function calculateCapRate(noi: number, purchasePrice: number): number {
  if (purchasePrice <= 0) return 0
  return (noi / purchasePrice) * 100
}

/**
 * Calculate Cash-on-Cash Return
 */
export function calculateCashOnCash(annualCashFlow: number, totalCashInvested: number): number {
  if (totalCashInvested <= 0) return 0
  return (annualCashFlow / totalCashInvested) * 100
}

/**
 * Calculate Gross Rent Multiplier
 */
export function calculateGRM(purchasePrice: number, annualRent: number): number {
  if (annualRent <= 0) return 0
  return purchasePrice / annualRent
}

/**
 * Calculate Break-Even Rent (rent needed to cover all expenses)
 */
export function calculateBreakEvenRent(monthlyExpenses: number, vacancyRate: number): number {
  if (vacancyRate >= 100) return Infinity
  return monthlyExpenses / (1 - vacancyRate / 100)
}

/**
 * Calculate monthly cash flow
 */
export function calculateMonthlyCashFlow(
  monthlyRent: number,
  vacancyRate: number,
  monthlyExpenses: number,
  monthlyDebtService: number
): number {
  const effectiveRent = monthlyRent * (1 - vacancyRate / 100)
  return effectiveRent - monthlyExpenses - monthlyDebtService
}

/**
 * Calculate equity after refinance (BRRRR)
 */
export function calculateEquityAfterRefi(arv: number, refiLTV: number, totalInvested: number): number {
  const newLoanAmount = arv * (refiLTV / 100)
  return newLoanAmount - totalInvested
}

/**
 * Calculate future home value with appreciation
 */
export function calculateFutureValue(currentValue: number, appreciationRate: number, years: number): number {
  return currentValue * Math.pow(1 + appreciationRate / 100, years)
}

/**
 * Calculate equity built over time (principal paydown + appreciation)
 */
export function calculateEquityBuilt(homeValue: number, remainingLoanBalance: number): number {
  return homeValue - remainingLoanBalance
}

/**
 * Calculate remaining loan balance after n payments
 */
export function calculateRemainingBalance(
  principal: number,
  annualRate: number,
  termYears: number,
  paymentsMade: number
): number {
  if (annualRate === 0) {
    return principal - (principal / (termYears * 12)) * paymentsMade
  }

  const monthlyRate = annualRate / 100 / 12
  const numPayments = termYears * 12

  const balance =
    principal *
    ((Math.pow(1 + monthlyRate, numPayments) - Math.pow(1 + monthlyRate, paymentsMade)) /
      (Math.pow(1 + monthlyRate, numPayments) - 1))

  return Math.max(0, balance)
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Format percentage for display
 */
export function formatPercent(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)}%`
}
